import { useState, useEffect } from 'react';
import {
  Card,
  Space,
  Button,
  Form,
  Input,
  DatePicker,
  Alert,
  message,
  Spin,
  Typography,
  Badge,
  Switch
} from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { trpc } from '../../../lib/api';
import { PageHeader, PageContainer } from '../../../components/common';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const maintenanceSettingsSchema = z.object({
  estimatedEndTime: z.string().nullable(),
  messageJa: z.string().min(1, '日本語メッセージを入力してください').max(500),
  messageEn: z.string().min(1, '英語メッセージを入力してください').max(500),
});

type MaintenanceSettingsFormValues = z.infer<typeof maintenanceSettingsSchema>;

const defaultMessages = {
  ja: 'システムメンテナンス中です。しばらくお待ちください。',
  en: 'System is currently under maintenance. Please try again later.',
};

export function MaintenanceModePage() {
  const [messageApi, contextHolder] = message.useMessage();
  // tRPC モック環境でも落ちないように useContext 呼び出しを安全化
  const utils = trpc.useContext?.();
  const [isQuickToggling, setIsQuickToggling] = useState(false);

  const getConfigQuery = trpc.maintenanceMode.get.useQuery(undefined, {
    refetchInterval: 180000 // 3分ごとにチェック
  });

  const updateConfigMutation = trpc.maintenanceMode.update.useMutation({
    onSuccess: (updatedConfig) => {
      utils?.maintenanceMode?.get?.setData?.(undefined, updatedConfig);
      messageApi.success('メンテナンスモード設定を更新しました');
      getConfigQuery.refetch();
    },
    onError: (error) => {
      console.error('Maintenance update error:', error);
      messageApi.error(error.message || 'メンテナンスモード設定の更新に失敗しました');
    }
  });

  const [isTogglingNormal, setIsTogglingNormal] = useState(false);
  const [isTogglingAgency, setIsTogglingAgency] = useState(false);

  const settingsForm = useForm<MaintenanceSettingsFormValues>({
    resolver: zodResolver(maintenanceSettingsSchema),
    defaultValues: {
      estimatedEndTime: null,
      messageJa: defaultMessages.ja,
      messageEn: defaultMessages.en,
    }
  });

  const config = getConfigQuery.data;
  const isEnvForced = config?.source === 'env';
  const isMaintenanceActive = config?.enabled || false;

  useEffect(() => {
    if (config) {
      settingsForm.reset({
        estimatedEndTime: config.estimatedEndTime,
        messageJa: config.message.ja || defaultMessages.ja,
        messageEn: config.message.en || defaultMessages.en,
      });
    }
  }, [config, settingsForm]);

  const handleToggleNormalUsers = async (enable: boolean) => {
    setIsTogglingNormal(true);
    try {
      const newTargets = {
        normalUsers: enable,
        agencyUsers: config?.targets.agencyUsers ?? false
      };

      // 両方のターゲットがfalseになる場合、メンテナンスモードを完全にOFFにする
      const newEnabled = newTargets.normalUsers || newTargets.agencyUsers;

      await updateConfigMutation.mutateAsync({
        enabled: newEnabled,
        startTime: newEnabled ? new Date().toISOString() : null,
        estimatedEndTime: newEnabled ? config?.estimatedEndTime || null : null,
        message: {
          ja: config?.message.ja || defaultMessages.ja,
          en: config?.message.en || defaultMessages.en
        },
        targets: newTargets,
        reason: enable ? '通常ユーザーのメンテナンスを開始' : '通常ユーザーのメンテナンスを解除'
      });
    } catch (error) {
      console.error('Failed to toggle normal users maintenance:', error);
    } finally {
      setIsTogglingNormal(false);
    }
  };

  const handleToggleAgencyUsers = async (enable: boolean) => {
    setIsTogglingAgency(true);
    try {
      const newTargets = {
        normalUsers: config?.targets.normalUsers ?? false,
        agencyUsers: enable
      };

      // 両方のターゲットがfalseになる場合、メンテナンスモードを完全にOFFにする
      const newEnabled = newTargets.normalUsers || newTargets.agencyUsers;

      await updateConfigMutation.mutateAsync({
        enabled: newEnabled,
        startTime: newEnabled ? new Date().toISOString() : null,
        estimatedEndTime: newEnabled ? config?.estimatedEndTime || null : null,
        message: {
          ja: config?.message.ja || defaultMessages.ja,
          en: config?.message.en || defaultMessages.en
        },
        targets: newTargets,
        reason: enable ? '代理店ユーザーのメンテナンスを開始' : '代理店ユーザーのメンテナンスを解除'
      });
    } catch (error) {
      console.error('Failed to toggle agency users maintenance:', error);
    } finally {
      setIsTogglingAgency(false);
    }
  };

  const handleQuickToggle = async (enable: boolean) => {
    setIsQuickToggling(true);
    try {
      const message = {
        ja: config?.message?.ja ?? defaultMessages.ja,
        en: config?.message?.en ?? defaultMessages.en,
      };

      const targets = config?.targets ?? { normalUsers: true, agencyUsers: true };

      await updateConfigMutation.mutateAsync({
        enabled: enable,
        startTime: enable ? new Date().toISOString() : null,
        estimatedEndTime: enable ? config?.estimatedEndTime || null : null,
        message,
        targets,
        reason: enable ? 'クイックトグルで有効化' : 'クイックトグルで無効化'
      });
    } catch (error) {
      console.error('Failed to toggle maintenance mode:', error);
    } finally {
      setIsQuickToggling(false);
    }
  };

  const onSubmitSettings = async (data: MaintenanceSettingsFormValues) => {
    let finalEstimatedEndTime = data.estimatedEndTime;
    if (finalEstimatedEndTime) {
      const now = new Date();
      const end = new Date(finalEstimatedEndTime);
      if (end.getTime() <= now.getTime() + 60000) {
        finalEstimatedEndTime = null;
      }
    }

    await updateConfigMutation.mutateAsync({
      enabled: isMaintenanceActive,
      startTime: isMaintenanceActive ? new Date().toISOString() : null,
      estimatedEndTime: finalEstimatedEndTime,
      message: {
        ja: data.messageJa,
        en: data.messageEn
      },
      targets: {
        normalUsers: config?.targets.normalUsers ?? false,
        agencyUsers: config?.targets.agencyUsers ?? false
      },
      reason: 'メッセージ設定を更新'
    });
  };

  if (getConfigQuery.isLoading) {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin tip="読み込み中..." size="large">
              <div style={{ minHeight: 100 }} />
            </Spin>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <PageContainer style={{ maxWidth: 1000, margin: '0 auto' }}>
      {contextHolder}

      <PageHeader
        title="メンテナンスモード設定"
        subtitle="サービスのメンテナンスモードを管理します。メンテナンスモードを有効にすると、対象ユーザーにメンテナンス画面が表示されます。"
      />

      {isEnvForced && (
        <Alert
          message="環境変数で強制有効化"
          description="メンテナンスモードは環境変数で強制的に有効化されています。管理画面から変更できません。"
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* メンテナンスモード制御 */}
      <Card
        title={
          <Space>
            <Text strong style={{ fontSize: 16 }}>メンテナンスモード制御</Text>
            {isMaintenanceActive ? (
              <Badge status="error" text="稼働中" />
            ) : (
              <Badge status="success" text="停止中" />
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card
            size="small"
            style={{ backgroundColor: '#f8f9fa', borderColor: '#d9d9d9' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space align="center">
                <Text strong>現在の状態:</Text>
                {isMaintenanceActive ? (
                  <Text type="warning" className="ant-typography-warning">メンテナンス中</Text>
                ) : (
                  <Text>通常稼働中</Text>
                )}
              </Space>
              <Space align="center">
                <Text strong>クイック切り替え:</Text>
                <Switch
                  aria-label="クイック切り替え"
                  checked={isMaintenanceActive}
                  onChange={handleQuickToggle}
                  loading={isQuickToggling || updateConfigMutation.isPending}
                  disabled={isEnvForced}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
              </Space>
            </Space>
          </Card>

          {/* 通常ユーザー */}
          <Card
            size="small"
            style={{
              backgroundColor: config?.enabled && config?.targets.normalUsers ? '#fff7e6' : '#f0f5ff',
              borderColor: config?.enabled && config?.targets.normalUsers ? '#ffa940' : '#91d5ff',
            }}
          >
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                {config?.enabled && config?.targets.normalUsers ? (
                  <WarningOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
                ) : (
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                )}
                <div>
                  <Space>
                    <Text strong>通常ユーザー</Text>
                    {config?.enabled && config?.targets.normalUsers ? (
                      <Badge status="error" text="メンテナンス中" />
                    ) : (
                      <Badge status="success" text="通常稼働" />
                    )}
                  </Space>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      一般企業ユーザー向けサービス
                    </Text>
                  </div>
                </div>
              </Space>
              <Switch
                checked={config?.enabled && config?.targets.normalUsers}
                onChange={handleToggleNormalUsers}
                loading={isTogglingNormal}
                disabled={isEnvForced}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Card>

          {/* 代理店ユーザー */}
          <Card
            size="small"
            style={{
              backgroundColor: config?.enabled && config?.targets.agencyUsers ? '#fff7e6' : '#f0f5ff',
              borderColor: config?.enabled && config?.targets.agencyUsers ? '#ffa940' : '#91d5ff',
            }}
          >
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                {config?.enabled && config?.targets.agencyUsers ? (
                  <WarningOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
                ) : (
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                )}
                <div>
                  <Space>
                    <Text strong>代理店ユーザー</Text>
                    {config?.enabled && config?.targets.agencyUsers ? (
                      <Badge status="error" text="メンテナンス中" />
                    ) : (
                      <Badge status="success" text="通常稼働" />
                    )}
                  </Space>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      代理店ポータル向けサービス
                    </Text>
                  </div>
                </div>
              </Space>
              <Switch
                checked={config?.enabled && config?.targets.agencyUsers}
                onChange={handleToggleAgencyUsers}
                loading={isTogglingAgency}
                disabled={isEnvForced}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Card>

          {!isEnvForced && (
            <Alert
              message="メンテナンスモードについて"
              description="各スイッチでユーザータイプごとにメンテナンスモードを制御できます。下記で設定したメッセージがユーザーに表示されます。"
              type="info"
              showIcon
              banner
            />
          )}
        </Space>
      </Card>

      {/* 設定フォーム */}
      <Card
        title={<Text strong style={{ fontSize: 16 }}>メッセージ設定</Text>}
      >
        <Form layout="vertical" onFinish={settingsForm.handleSubmit(onSubmitSettings)}>
          <Alert
            message="設定の保存について"
            description="これらの設定は保存後、次回のメンテナンスモード開始時または現在稼働中の場合は即座に反映されます。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="終了予定時刻（任意）"
            help="メンテナンス終了の目安を設定できます"
          >
            <Controller
              name="estimatedEndTime"
              control={settingsForm.control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date ? date.toISOString() : null)}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                  disabled={isEnvForced}
                  placeholder="終了予定時刻を選択（任意）"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label="メンテナンスメッセージ（日本語）"
            validateStatus={settingsForm.formState.errors.messageJa ? 'error' : ''}
            help={settingsForm.formState.errors.messageJa?.message}
            required
          >
            <Controller
              name="messageJa"
              control={settingsForm.control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={3}
                  maxLength={500}
                  disabled={isEnvForced}
                  status={settingsForm.formState.errors.messageJa ? 'error' : ''}
                  placeholder="ユーザーに表示される日本語メッセージ"
                  showCount
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label="メンテナンスメッセージ（英語）"
            validateStatus={settingsForm.formState.errors.messageEn ? 'error' : ''}
            help={settingsForm.formState.errors.messageEn?.message}
            required
          >
            <Controller
              name="messageEn"
              control={settingsForm.control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={3}
                  maxLength={500}
                  disabled={isEnvForced}
                  status={settingsForm.formState.errors.messageEn ? 'error' : ''}
                  placeholder="Message displayed to users in English"
                  showCount
                />
              )}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateConfigMutation.isPending}
                disabled={isEnvForced}
                size="large"
              >
                設定を保存
              </Button>
              <Button
                onClick={() => getConfigQuery.refetch()}
                size="large"
              >
                再読み込み
              </Button>
            </Space>
            {settingsForm.formState.isDirty && (
              <div style={{ marginTop: 8 }}>
                <Text type="warning" style={{ fontSize: 12 }}>
                  ※ 未保存の変更があります
                </Text>
              </div>
            )}
          </Form.Item>
        </Form>
      </Card>
    </PageContainer>
  );
}
